//Form to allow coding of a single Victim Form (erroneously named IRespondent).
import { Form, Col, Row } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import '../../css/App.css';
import '../../css/Coding.css';
import { FormInput } from '../form/Input';
import { FormSelect } from '../form/Select';
import { FormTextArea } from '../form/TextArea';
import { Roles, Status } from '../../enums';
import { ReactElement, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { t, isNullOrUndefined } from '../../helpers';
import { VictimForm } from '../victim-form/VictimForm';
import NewWindow from 'rc-new-window';
import { IValidation } from '../../types';
import { useRole } from '../../hooks';
import { getCodingStatusLabel } from '../../helpers';


export const CodingForm = (props: any): ReactElement => {
  const { field, codeData = [], control, index, isFormReadOnly, respCodingStatus } = props;
  
  //vfCodingStatus is the coding action required for the particular Victim Form (erroneously named IRespondent).
  //respCodingStatus (see previous line of code) is the Respondent's coding action.
  //e.g. if respCodingStatus is ONSCoding, vfCodingStatus could be:
  // ONSCoding (if it requires ONS to code it)
  // Complete (if it has already been definitvely coded by Verian coders)
  const vfCodingStatus = field.Coding_Status;
  const vfCodingLabel = getCodingStatusLabel(field.Coding_Status);

  const { accounts } = useMsal();
  const username = accounts[0]?.username.split("@", 1);

  const certain = [
    { value: false, label: 'Certain' },
    { value: true, label: 'Uncertain' },
  ];
  const cyberYesNoAndCertaintyValues = [
    { value: 0, label: 'Cyber Certain' },
    { value: 1, label: 'Cyber Uncertain' },
    { value: 2, label: 'Not Cyber Certain' },
    { value: 3, label: 'Not Cyber Uncertain' },
  ];
  const cyberYesNoValues = [
    { value: 0, label: 'Not Cyber' },
    { value: 1, label: 'Cyber' },
  ];

  const modifyValidationFunction = (validationFunction: (value: any) => IValidation, isReadOnly: boolean) => {
    if (isReadOnly) {
      return () => true;
    }
    return validationFunction;
  }

  const createValidationOnValueExists = (message: string) => ((value: any): IValidation  => {
    if(isNullOrUndefined(t(value))) {
      return message;
    }
    return true;
  });


  const yesNo = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ];
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { role } = useRole();
  const isKawiButtonVisible = role !== Roles.ONS;

  const isVerianFieldVisible = vfCodingStatus !== Status.Coding;

  const isSupervisorVisible = vfCodingStatus === Status.Supervisor;

  const isONSVisible =
    respCodingStatus === Status.ONSReview ||
    respCodingStatus === Status.ONSProCoding ||
    respCodingStatus === Status.ONSCoding ||
    vfCodingStatus === Status.ExecReview ||
    respCodingStatus === Status.ExecReview ||
    vfCodingStatus === Status.Review;

  const showWhyCodingReason =
    respCodingStatus === Status.ONSProCoding ||
    respCodingStatus === Status.ONSCoding;

  const isProvisionalVisible = respCodingStatus === Status.ONSCoding;

  const isAcceptONSVisible = (vfCodingStatus === Status.ExecReview || vfCodingStatus === Status.Review) && respCodingStatus !== Status.ONSProCoding;

  const showDialog = vfCodingStatus !== Status.Coding;

  const isTNSCoderUncertainVisible = vfCodingStatus === Status.Supervisor;

  const isTNSCyberVisible = vfCodingStatus !== Status.Supervisor && vfCodingStatus !== Status.Coding;

  const isTNSCoderCyberVisible = vfCodingStatus === Status.Supervisor;

  const data = useWatch({
    control,
    name: `respondents.${index}`,
  });

  // Mirrors OCdeValidator.cs: Form_Classification cannot be relied on as 'U' overrides 'Q' and 'R',
  // so a form that is both uncertain and Q/R reads as Q/R here.
  const isQualityCheckOrReferral = data?.Requires_Quality_Check || data?.Is_Referral_Code;
  const isONSCodeMandatory = t(data?.TNS_Is_Uncertain) === true || isQualityCheckOrReferral;
  const isONSCyberMandatory = t(data?.TNS_Cyber_Flag_Is_Uncertain) === true || isQualityCheckOrReferral;

  // R23: Status and Type aren't enough to tell the ONS what's in question on their own - Type just
  // shows Form_Classification, which reads 'U' whether the uncertainty is in the code, the cyber
  // flag, or both. Split that out; Q/R read straight off the classification like isQualityCheckOrReferral.
  const getWhyCoding = (): string | null => {
    if (!showWhyCodingReason) {
      return null;
    }
    const isCodeUncertain = t(data?.TNS_Is_Uncertain) === true;
    const isCyberUncertain = t(data?.TNS_Cyber_Flag_Is_Uncertain) === true;

    const statements: string[] = [];
    if (isCodeUncertain && isCyberUncertain) {
      statements.push('Verian are uncertain about both the offence code and the cyber flag.');
    } else if (isCodeUncertain) {
      statements.push('Verian are uncertain about the offence code.');
    } else if (isCyberUncertain) {
      statements.push('Verian are uncertain about the cyber flag.');
    }
    if (data?.Requires_Quality_Check === true) {
      statements.push('This form requires a quality check.');
    } else if (data?.Is_Referral_Code === true) {
      statements.push('This form has been referred to the ONS by Verian.');
    } else if (data?.Form_Classification === 'U') {
    } else  {
      statements.push('This form does not require any action, but you can code it if you would like to.');
    }
    
    return statements.length > 0 ? statements.join(' ') : null;
  };
  const whyCoding = getWhyCoding();

  const getVerianSelectSize = (): number => {
    if (isTNSCoderUncertainVisible && isTNSCoderCyberVisible) {
      return 7;
    }
    if (isTNSCoderUncertainVisible || isTNSCoderCyberVisible || isTNSCyberVisible) {
      return 7;
    }
    return 11;
  };

  const commentValidation = (value: any): IValidation => {
    if ((vfCodingStatus === Status.Supervisor)
      && t(data.TNS_Super_Code) !== t(data.TNS_Coder_Code)
      && t(value) === null
    ) {
      return "You must add a comment as your code differs from the original coder's";
    }

    if ((vfCodingStatus === Status.Review)
      && t(data.TNS_Accept_ONS_Code) === false
      && t(value) === null
    ) {
      return "You must add a comment as you have rejected the ONS's coding";
    }
    if ((vfCodingStatus === Status.ONSReview) && (t(value) === null)) {
      return 'You must enter a comment in response to Verian';
    }

    if ((respCodingStatus === Status.ONSCoding || vfCodingStatus === Status.ONSProCoding)
      && t(value) === null
      && ((t(data.ONS_Code) !== null && t(data.ONS_Code) !== t(data.TNS_Code))
        || (t(data.ONS_Cyber_Flag) !== null && t(data.ONS_Cyber_Flag) !== t(data.TNS_Cyber_Flag)))
    ) {
      return "You must add a comment as your code and/or cyber flag differs from Verian's";
    }

    return true;
  };

  const acceptONSValidation = (value: string | null): IValidation => {
    if ((vfCodingStatus === Status.Review)) {
      if ((t(value) === null)) {
        return "You must accept or reject the ONS's coding";
      }
      return true;
    }
    // deliberately return undefined. In Ivalidation it means, that field is disabled.
    return undefined;
  };

  const ONSCodeIsProvisionalValidation = (value: string | null): IValidation => {
    if (!isProvisionalVisible) {
      return true;
    }
    const recordedEither = t(data.ONS_Code) !== null || t(data.ONS_Cyber_Flag) !== null;
    if (recordedEither && t(value) === null) {
      return 'You must select whether to mark as provisional';
    }
    if (!recordedEither && t(value) !== null) {
      return 'You must clear the provisional flag as you have not recorded a code or cyber flag';
    }
    return true;
  };

  const finalCyberFlagValidation = (value: any): IValidation => {
    if (vfCodingStatus === Status.ExecReview) {
      if (t(value) === null && t(data.Push_To_ONS) !== true) {
        return 'You must either set the final code and cyber flag, or push to the ONS';
      }
      return true;
    }
    // deliberately return undefined. In Ivalidation it means, that field is disabled.
    return undefined;
  };
  const codeONSValidation = (value: any): IValidation => {
    if (vfCodingStatus === Status.ONSCoding || vfCodingStatus === Status.ONSProCoding) {
      if (t(value) === null && isONSCodeMandatory) {
        return 'You must select a code';
      }
      return true;
    }
    if (respCodingStatus === Status.ONSCoding && vfCodingStatus !== Status.ONSCoding ) {
      return true;
    }
    // deliberately return undefined. In Ivalidation it means, that field is disabled.
    return undefined;
  };
  const onsCyberValidation = (value: any): IValidation => {
    if (vfCodingStatus === Status.ONSCoding || vfCodingStatus === Status.ONSProCoding) {
      if (t(value) === null && isONSCyberMandatory) {
        return 'You must select a cyber flag';
      }
      return true;
    }
    if (respCodingStatus === Status.ONSCoding && vfCodingStatus !== Status.ONSCoding ) {
      return true;
    }
    // deliberately return undefined. In Ivalidation it means, that field is disabled.
    return undefined;
  };
  const finalCodeValidation = (value: any): IValidation => {
    if ((vfCodingStatus === Status.ExecReview)
      && t(value) === null
      && t(data.Push_To_ONS) !== true
    ) {
      return 'You must either set the final code and cyber flag, or push to the ONS';
    }
    return true;
  };
  const pushToONSValidation = (value: any): IValidation => {
    if ((vfCodingStatus === Status.ExecReview)
      && (t(data.Final_Code) !== null || t(data.Final_Cyber_Flag) !== null)
      && t(value) === true
    ) {
      return 'If you set the final code and/or cyber flag, you cannot also push it to the ONS';
    }
    return true;
  };

  const handleOpenCawi = (): void => {
    window.open(field.CAWILink+"&uid="+username, '_blank', 'location=yes,scrollbars=yes,status=yes');
  };

  const handleOpenForm = (): void => {
    setIsPopupOpen(true);
  };

  return (
    <>
      <Form.Group as={Row} className="mb-3 csew-form-group">
        <Col>
          <button onClick={handleOpenForm} type="button" className="csew-button csew-button_small">
            Open Form
          </button>
          {isKawiButtonVisible && (
            <button onClick={handleOpenCawi} type="button" className="csew-margin-left csew-button csew-button_secondary csew-button_small">
              Start CAWI
            </button>
          )}
        </Col>
        <Form.Label column sm={1} className="csew-text-align-right">
          Status
        </Form.Label>
        <Col sm={3}>
          <Form.Control
            disabled
            value={vfCodingLabel}
          />
        </Col>
        <FormInput size={1} {...props} name="Form_Classification" label="Type" disabled={true} />
      </Form.Group>

      <Form.Group as={Row} className="mb-3 csew-form-group">
        <FormInput size={2} {...props} name="Serial_Num" label="Serial" disabled={true} />
        <FormInput {...props} name="Offence_Snippet" label="Description" disabled={true} />
      </Form.Group>
      {isVerianFieldVisible && (
        <Form.Group as={Row} className="mb-3 csew-form-group">
          <FormSelect
            // disabled attribute should be added conditionally. bug 84661
            {...(isFormReadOnly ? { disabled: true } : {} )}
            size={getVerianSelectSize()}
            {...props}
            name="TNS_Code"
            label={vfCodingStatus === Status.Supervisor ? 'Coder' : 'Verian'}
            options={codeData}
          />
          {isTNSCoderUncertainVisible && (
          <FormSelect 
            {...(isFormReadOnly ? { disabled: true } : {} )} 
            {...props} 
            name="TNS_Coder_Is_Uncertain"
            options={certain} 
           />)}
          {isTNSCoderCyberVisible && (
          <FormSelect 
            {...props} 
            {...(isFormReadOnly ? { disabled: true } : {} )} 
            name="TnsCoderCyberCertain" 
            options={cyberYesNoAndCertaintyValues} 
          />)}
          {isTNSCyberVisible && (
          <FormSelect 
            size={2}
            {...props} 
            {...(isFormReadOnly ? { disabled: true } : {} )} 
            name="TNS_Cyber_Flag" 
            options={cyberYesNoValues} 
          />)}
        </Form.Group>
      )}
      {isSupervisorVisible && (
        <Form.Group as={Row} className="mb-3 csew-form-group">
          <FormSelect
            size={7}
            {...props}
            name="TNS_Super_Code"
            label="Supervisor"
            options={codeData}
            {...(isFormReadOnly ? { disabled: true } : {} )}
            validate={modifyValidationFunction(
              createValidationOnValueExists('You must select an offence code and whether you are certain or uncertain of it'), 
              isFormReadOnly
            )}
          />
          <FormSelect  
            {...(isFormReadOnly ? { disabled: true } : {} )} 
            {...props} 
            name="TNS_Super_Is_Uncertain" 
            options={certain} 
            validate={modifyValidationFunction(
              createValidationOnValueExists('You must select certain or uncertain of it'), 
              isFormReadOnly
            )}
          />
          <FormSelect
            {...props}
            {...(isFormReadOnly ? { disabled: true } : {} )}
            name="TnsSuperCyberCertain"
            options={cyberYesNoAndCertaintyValues}
            validate={modifyValidationFunction(
              createValidationOnValueExists('You must select a cyber flag and whether you are certain or uncertain of it'), 
              isFormReadOnly
            )}
          />
        </Form.Group>
      )}
      {whyCoding && (
        <Form.Group as={Row} className="mb-1 csew-form-group">
          <Col>
            <Form.Text>{whyCoding}</Form.Text>
          </Col>
        </Form.Group>
      )}
      {isONSVisible && (
        <Form.Group as={Row} className="mb-3 csew-form-group">
          <FormSelect
            size={7}
            {...props}
            {...(isFormReadOnly ? { disabled: true } : {} )}
            name="ONS_Code"
            label="ONS"
            options={codeData}
            validate={modifyValidationFunction(codeONSValidation, isFormReadOnly)}
            defaultValue={field.TNS_Code}
          />
          <FormSelect
            size={2}
            {...props}
            {...(isFormReadOnly ? { disabled: true } : {} )}
            name="ONS_Cyber_Flag"
            options={cyberYesNoValues}
            validate={modifyValidationFunction(onsCyberValidation, isFormReadOnly)}
          />
          {isProvisionalVisible && (
            <FormSelect 
            {...props} 
              {...(isFormReadOnly ? { disabled: true } : {} )} 
              name="ONS_Code_Is_Provisional" 
              label="Provisional" 
              options={yesNo} 
              validate={modifyValidationFunction(ONSCodeIsProvisionalValidation, isFormReadOnly)} 
            />
          )}
          {isAcceptONSVisible && (
            <FormSelect 
              {...props} 
              {...(isFormReadOnly ? { disabled: true } : {} )} 
              name="TNS_Accept_ONS_Code" 
              label="V. Accept" 
              options={yesNo} 
              validate={modifyValidationFunction(acceptONSValidation, isFormReadOnly)} 
            />
          )}
        </Form.Group>
      )}
      {(vfCodingStatus === Status.ExecReview || respCodingStatus === Status.ExecReview) && (
        <Form.Group as={Row} className="mb-3 csew-form-group">
          <FormSelect
            size={7}
            {...props}
            name="Final_Code"
            label="Final"
            options={codeData}
            disabled={isFormReadOnly}
            validate={modifyValidationFunction(finalCodeValidation, isFormReadOnly)}
          />
          <FormSelect
            size={2}
            {...props}
            {...(isFormReadOnly ? { disabled: true } : {})}
            name="Final_Cyber_Flag"
            options={cyberYesNoValues}
            disabled={isFormReadOnly}
            validate={modifyValidationFunction(finalCyberFlagValidation, isFormReadOnly)}
          />
          <FormSelect 
            {...props} 
            {...(isFormReadOnly ? { disabled: true } : {} )} 
            name="Push_To_ONS" 
            label="Push > ONS" 
            options={yesNo} 
            validate={modifyValidationFunction(pushToONSValidation, isFormReadOnly)} 
            hasNoEmptyOption 
          />
        </Form.Group>
      )}
      <Form.Group as={Row} className="mb-3 csew-form-group">
        <FormTextArea 
          {...props} 
          {...(isFormReadOnly ? { disabled: true } : {} )} 
          name="Comment" 
          label="Comments"
          showDialog={showDialog} 
          validate={modifyValidationFunction(commentValidation, isFormReadOnly)} 
        />
      </Form.Group>
      {isPopupOpen && (
        <NewWindow onClose={() => setIsPopupOpen(false)}>
          <VictimForm data={field} />
        </NewWindow>
      )}
    </>
  );
};
